import { useState, useEffect, useRef, useContext } from 'react';
import classNames from 'classnames';
import { Link } from "react-router-dom";
import { CSSTransition as ReactTransitionGroupCssTransition } from "react-transition-group";
import { useTranslation } from "react-i18next";
import LoginDropdown from './LoginDropdown';
import HeaderMobileComponent from "../companyProfileToDo/MobileViewComp";
import { body, isNotUndefined, consolidatedHeaderObject, getViewportIsMobile } from "@/shared/FunctionCore";
import homepageLogoUrl from "@/resources/original/logo-dnb.svg";
import "@/styles/AppHeaderStyles.scss";
import { AppContext, ContextType } from '@/shared/context';

const AppHeader = ({
	isTaiwan,
	loginMenuOpen,
	setLoginMenuOpen,
	countryCode
}: {
	isTaiwan: boolean,
	loginMenuOpen: boolean,
	setLoginMenuOpen: any,
	countryCode: string
}): JSX.Element => {
	const { t } = useTranslation();
	const { searchResultsContainerReference } = useContext(AppContext) as ContextType
	const [showSearchOption, setShowSearchOption] = useState(false);
	const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);
	const consolidatedHeaderProductLoginReferenceHtmlInnerWrapperReference = useRef<HTMLDivElement>(null);
	const [viewportIsMobile, setViewportIsMobile] = useState(false);
	const homeRedirection = t("homeRedirection");
	const enterpriseTitle = t("businessDirectory.enterpriseTitle");
	const smallBusinessTitle = t("businessDirectory.smallBusinessTitle");
	const searchOptionsTitle = t("searchOptions");
	const siteContentTitle = t("businessDirectory.siteContentTab");
	const siteContentLink = t("businessDirectory.siteContentLink");
	const dunsNumberTitle = t("businessDirectory.dunsNumberTitle");
	const businessDirectoryTitle = t("businessDirectory.businessDirectoryTitle");
	const serviceCenterTitle = t("businessDirectory.serviceCenterTitle");
	const loginTitle = t("businessDirectory.loginTitle");
	const publicSectorTitle = t("publicSectorTitle");
	const bdLandingPageRedirectionLink = t("businessDirectory.bdLandingPageRedirectionLink");
	const smallBusinessLink = t("businessDirectory.smallBusinessLink");
	const companyProfileTab = t('businessDirectory.companyProfileTab');
	const companyProfileLink = t('businessDirectory.companyProfileLink');
	const businessCreditURL = t('businessCreditURL');
	const businessGrowthURL = t('businessGrowthURL');
	const businessRiskURL = t('businessRiskURL');
	const dunsNumberURL = t('dunsNumberURL');
	const enterpriseURL = t('enterpriseURL');
	const globeHeaderIconURL = t('globeHeaderIconURL');
	const publicSectorURL = t('publicSectorURL');
	const resourcesHeaderURL = t('resourcesHeaderURL');
	const serviceCenterURL = t('serviceCenterURL');
	const contactProfilesPage = t('contactProfilesPage');
	const marketPlacePage = t('marketPlacePage');

	useEffect(() => {
		if (!loginMenuOpen) {
			closeProductLogin()
		} else if (showSearchOption) {
			setShowSearchOption(false)
		}
	}, [loginMenuOpen])

	useEffect(() => {
		const $body = $(body);
		const $app_page = $("#app_page");
		const $window = $(window);

		$body.on("click", (cl) => {
			const target = cl.target;

			const $targetAndParents = $(target).parents().addBack();

			if (
				$targetAndParents.filter(`.${'consolidatedHeaderLoginButton'[1]}`).length ===
				0 &&
				$targetAndParents.filter(
					`.${'search-dropdown-menu'[1]}`
				).length === 0
			) {
				setShowSearchOption(false);
			}
		});

		const $productLoginDropDown = $("#productLoginDropDown");

		$productLoginDropDown.on("click", (cl) => {
			const afv = isNotUndefined(cl.target.id) && cl.target.id === "productLoginCloseButton"

			if (!afv) cl.stopPropagation();
		});

		// ===================================

		const $consolidatedHeaderInnerWrapper = $(`.consolidatedHeaderInnerWrapper`);
		const $consolidatedHeaderWrapper = $(".consolidatedHeaderWrapper");

		let headerHeight = 0;
		let headerTransitionClassAdded = false;
		let isFirstNonMobileScrollPastHeaderHeight = true;
		let lastWindowPageYOffset = 0;

		const resetToInitialState = () => {
			headerTransitionClassAdded = false;
			isFirstNonMobileScrollPastHeaderHeight = true;

			consolidatedHeaderObject.headerPosition = false;

			$consolidatedHeaderWrapper.removeClass("position");
			$consolidatedHeaderWrapper.removeClass("transition");

			$consolidatedHeaderWrapper.css("top", 0);
			$app_page.css("margin-top", 0);
		};

		const setHeaderHeight = () => {
			// Measure header
			const consolidatedHeaderInnerWrapperOuterHeight = $consolidatedHeaderInnerWrapper.outerHeight();

			if (isNotUndefined(consolidatedHeaderInnerWrapperOuterHeight)) {
				headerHeight = consolidatedHeaderInnerWrapperOuterHeight;
			}
		};

		const setStyles = () => {
			$consolidatedHeaderWrapper.css("top", consolidatedHeaderObject.showHeader ? 0 : -1 * (headerHeight + 52));
			$app_page.css("margin-top", headerHeight);
		};

		const updateStylesIfNecessary = () => {
			if (!isFirstNonMobileScrollPastHeaderHeight) {
				setStyles();
			}
		};

		consolidatedHeaderObject.onHeaderHeightChange = () => {
			setHeaderHeight();
			updateStylesIfNecessary();
		};

		setHeaderHeight();
		$window.on("scroll", () => {
			// Scroll handler for nonmobile viewports
			if (!getViewportIsMobile()) {
				const windowPageYOffset = window.pageYOffset;

				// Ignore scroll events that don't change the scroll position
				if (windowPageYOffset !== lastWindowPageYOffset) {
					// Determine scroll direction
					const scrollDirectionIsUp = windowPageYOffset < lastWindowPageYOffset;

					consolidatedHeaderObject.showHeader = scrollDirectionIsUp;

					// Save current scroll position
					lastWindowPageYOffset = windowPageYOffset;

					if (windowPageYOffset > headerHeight) {
						if (isFirstNonMobileScrollPastHeaderHeight) {
							isFirstNonMobileScrollPastHeaderHeight = false;

							consolidatedHeaderObject.headerPosition = true;

							$consolidatedHeaderWrapper.addClass("position");
						} else if (!headerTransitionClassAdded && scrollDirectionIsUp) {
							headerTransitionClassAdded = true;

							$consolidatedHeaderWrapper.addClass("transition");
						}

						setStyles();
					} else if (windowPageYOffset === 0) {
						resetToInitialState();
					}
				}
			}
		});

		const mediaQueryList = window.matchMedia("screen and (max-width: 991px)");
		// Initial check when component mounts
		checkViewportIsMobile(mediaQueryList);
		mediaQueryList.addEventListener("change", checkViewportIsMobile);

		// Clean up by removing the event listener on unmount
		return () => {
			mediaQueryList.removeEventListener("change", checkViewportIsMobile);
		};
	}, [])

	const checkViewportIsMobile = (me: any) => {
		const matches = me.matches;
		setViewportIsMobile(matches); // Update state
		document?.querySelector("html")?.classList.toggle("mobile", matches);
	};

	const toggleSearchOverlay = () => {
		setIsSearchOverlayOpen(!isSearchOverlayOpen);
	}

	const syncDropDowns = (current: string, flag: boolean) => {
		if (current === "optionsBox") {
			if (!flag && loginMenuOpen) { // is false means, its about to open and login menu is also open
				setLoginMenuOpen(false)
			}
		}
	}

	const closeProductLogin = () => {
		const current = consolidatedHeaderProductLoginReferenceHtmlInnerWrapperReference.current;

		if (current !== null) {
			const $current = $(current);

			$current.hide();

			setLoginMenuOpen(false);
		}
	};

	return (
		<ReactTransitionGroupCssTransition
			classNames={classNames("enterActive", "enterDone", "exit", "exitActive")}
			in={isSearchOverlayOpen}
			timeout={600}
		>
			<div ref={searchResultsContainerReference} >
				<div className={`consolidatedHeaderWrapper isTaiwan-${isTaiwan}`}>
					<div className="consolidatedHeaderInnerWrapper">
						{viewportIsMobile ? (
							<HeaderMobileComponent countryCode={countryCode} />

						) : (
							<>
								<header>
									<nav className="navBarMenuBlue text-white navbar navbar-expand-lg">
										<div className="container-fluid">
											<div className="collapse navbar-collapse" id="navbarScroll">
												<ul className="navbar-nav navbarMain me-auto navbar-nav-scroll">
													<li className="nav-item">
														<a className="nav-link" aria-current="page" href={enterpriseURL}>
															{enterpriseTitle}
														</a>
													</li>
													<li className="nav-item nav-link-always-active">
														<a className="nav-link active" href={smallBusinessLink}>
															{smallBusinessTitle}
														</a>
													</li>
													<li className="nav-item">
														<a className="nav-link" href={publicSectorURL}>
															{publicSectorTitle}
														</a>
													</li>
												</ul>
												<ul className="navbar-nav navbarSearch my-2 my-lg-0 navbar-nav-scroll">
													<li className="nav-item dropdown d-flex" onClick={(e) => {
														e.stopPropagation();
														setShowSearchOption((flag) => {
															syncDropDowns("optionsBox", flag)
															return !flag
														})
													}}>
														<a
															role="button"
															className={classNames({
																"nav-link d-flex": true,
																"show": showSearchOption
															})}
															type="button"
															data-bs-toggle="dropdown"
															aria-expanded={showSearchOption}
														>
															<div className="headerSearchIcon" />
															{searchOptionsTitle}
														</a>
														<ul className={classNames({
															"dropdown-menu search-dropdown-menu": true,
															"show": showSearchOption
														})}>
															<li>
																<a className="dropdown-item" href={siteContentLink}>
																	{siteContentTitle}
																</a>
															</li>
															{countryCode !== 'us' && (
																<li>
																	<Link className="dropdown-item" to={companyProfileLink}>
																		{companyProfileTab}
																	</Link>
																</li>
															)}
															{countryCode === 'us' && (
																<>
																	<li>
																		<Link className="dropdown-item" to={bdLandingPageRedirectionLink}>
																			{businessDirectoryTitle}
																		</Link>
																	</li>
																	<li>
																		<Link className="dropdown-item" to={contactProfilesPage}>
																			{t("contactDirectory.contact_directory")}
																		</Link>
																	</li>
																	<li>
																		<Link className="dropdown-item" to={marketPlacePage}>
																			{t("businessDirectory.marketplace")}
																		</Link>
																	</li>
																</>
															)}
														</ul>
													</li>
													<li className="nav-item">
														<a className="nav-link" href={serviceCenterURL}>
															{serviceCenterTitle}
														</a>
													</li>
													<li className="nav-item">
														<a className="nav-link" href={dunsNumberURL}>
															{dunsNumberTitle}
														</a>
													</li>
													<li className="nav-item">
														<Link className="nav-link" to={bdLandingPageRedirectionLink}>
															{businessDirectoryTitle}
														</Link>
													</li>
													<li className="nav-item">
														<a className="nav-link d-flex" onClick={(e) => {
															const current =
																consolidatedHeaderProductLoginReferenceHtmlInnerWrapperReference.current;
															e.stopPropagation();
															if (current !== null) {
																const $current = $(current);
																$current.slideToggle(100);
																setLoginMenuOpen((va: boolean) => !va);
															}
														}}
															role='button'
														>
															<div className="headerUserLoginIcon" />
															{loginTitle}
														</a>
													</li>
													{isTaiwan ? <></> :
														(<li className="nav-item">
															<a className="nav-link"
																aria-label="Choose Your Country"
																href={globeHeaderIconURL}
															>
																<div className="headerInternationalSiteSelector" />
															</a>
														</li>)}
												</ul>
											</div>
										</div>
									</nav>
								</header>
								<div className="consolidatedHeaderDesktopOuterWrapper">
									<div className="consolidatedHeaderDesktop">
										<div className="consolidatedHeaderDesktopInnerWrapper">
											<div className="consolidatedHeaderBeginningPadding" />
											<div className="consolidatedHeaderLogoWrapper">
												<a
													aria-label="Homepage link"
													href={homeRedirection}
													onClick={(e) => {
														e.preventDefault();
														window.location.href = homeRedirection;
													}}
												>
													{homepageLogoUrl ? (
														<img
															alt="D&B logo"
															className="consolidatedHeaderLogo"
															src={homepageLogoUrl}
														/>
													) : (
														<div
															aria-label="D&B logo"
															className={classNames(
																"consolidatedHeaderLogo",
																"consolidatedHeaderLogoBG"
															)}
															role="img"
														/>
													)}
												</a>
											</div>
											<div className="consolidatedHeaderMiddlePadding"></div>
											<div
												className="consolidatedHeaderSearchAndLinksWrapper"
											>
												<div className="consolidatedHeaderLinksWrapper">
													<div className="consolidatedHeaderLinkWrapper">
														<a
															className="consolidatedHeaderLink"
															href={dunsNumberURL}
														>
															{dunsNumberTitle}
														</a>
													</div>
													{countryCode === 'us' && (
														<>
															<div className="consolidatedHeaderLinkWrapper">
																<a
																	className="consolidatedHeaderLink"
																	href={businessCreditURL}
																>
																	Business Credit
																</a>
															</div>
															<div className="consolidatedHeaderLinkWrapper">
																<a
																	className="consolidatedHeaderLink"
																	href={businessGrowthURL}
																>
																	Business Growth
																</a>
															</div>
															<div className="consolidatedHeaderLinkWrapper">
																<a
																	className="consolidatedHeaderLink"
																	href={businessRiskURL}
																>
																	Business Risk
																</a>
															</div>
															<div className="consolidatedHeaderLinkWrapper">
																<a
																	className="consolidatedHeaderLink"
																	href={resourcesHeaderURL}
																>
																	Resources
																</a>
															</div>
														</>
													)}
												</div>
											</div>
											<div className="consolidatedHeaderEndPadding" />
										</div>
									</div>
								</div>
							</>
						)}

						<div className="consolidatedHeaderProductLoginReferenceHtmlWrapper" id="productLoginDropDown">
							<div className="consolidatedHeaderProductLoginReferenceHtmlInnerWrapper"
								ref={consolidatedHeaderProductLoginReferenceHtmlInnerWrapperReference}
							>
								<LoginDropdown setLoginMenuOpen={setLoginMenuOpen} countryCode={countryCode} />
							</div>
						</div>
					</div>
				</div>
			</div>
		</ReactTransitionGroupCssTransition>
	)
}

export default AppHeader